/**
 * Environment-based visibility configuration for AppsSidebar
 * 
 * Controls which sections and app categories are visible based on the environment.
 * This is independent of user role checks (those still apply when configured).
 * 
 * The user profile section (bottom area with user info, language, theme, logout)
 * is ALWAYS visible regardless of environment.
 */

export type Environment = 'development' | 'production';

export interface SectionVisibility {
  notifications: boolean;
  reports: boolean;
  store: boolean;
}

export interface EnvironmentVisibilityConfig {
  sections: SectionVisibility;
  /** 
   * List of category IDs that are visible in this environment.
   * Categories not in this list will be hidden.
   * Role-based checks still apply on top of this.
   */
  visibleCategories: string[];
}

type VisibilityConfigMap = Record<Environment, EnvironmentVisibilityConfig>;

/**
 * Visibility configuration per environment
 * 
 * Categories available:
 * - 'rastreo-telemetria': Fleet management / GPS tracking
 * - 'administracion': Administration (billing, services, etc.)
 * - 'point-of-sale': POS system
 * - 'internos': Internal systems (requires role check)
 * - 'crm': CRM
 * - 'marketing': Marketing (promoted from CRM Marketing)
 * - 'service-desk': Service Desk (promoted from CRM Soporte)
 * - 'access-control': Nuvex Access — control de acceso, seguridad y dispositivos
 * - 'comunity': Nuvex Access — comunidad, comunicación y pagos
 * - 'b2c': B2C — panel interno de operación de Movik (requires role check)
 */
export const VISIBILITY_CONFIG: VisibilityConfigMap = {
  development: {
    sections: {
      notifications: true,
      reports: true,
      store: false,
    },
    // Marketing/Soporte are promoted to their own categories
    // (marketing / service-desk).
    visibleCategories: [
      'rastreo-telemetria', // Gestión de Flota (Fleet Management)
      'point-of-sale',  // Punto de Venta
      'administracion', // Administración de la cuenta
      'crm',            // CRM
      'internos',       // Sistemas Internos (con validación de role)
      'marketing',      // Marketing (campañas, formularios, landing)
      'service-desk',   // Service Desk (Soporte)
      'envios',         // Gestión de Envíos
      'bases-y-propiedades', // Bases y Propiedades (Amenidades, Patio, Reservas)
      'access-control', // Nuvex Access — Control de acceso, seguridad y dispositivos
      'comunity',       // Nuvex Access — Comunidad, comunicación y pagos
      'cargo-market',   // Cargo Market — marketplace y cotizador de carga
      'freight-forwarding', // Freight forwarding — Documentación (expediente documental, CC-CONSOLE #1632)
      'gestion-staff',  // Gestión de Staff — Actividades con evidencia
      'finanzas',       // Finanzas — gastos atribuidos por Coding Cloud (CC-CONSOLE #981)
      'b2c',            // B2C — panel interno de Movik (sólo administradores de plataforma)
    ],
  },
  production: {
    sections: {
      notifications: true, // Alertas - siempre visible en prod
      reports: true,
      store: false,
    },
    // Same category set as development (kept in sync intentionally).
    visibleCategories: [
      'rastreo-telemetria', // Gestión de Flota (Fleet Management)
      'point-of-sale',  // Punto de Venta
      'administracion', // Administración de la cuenta
      'crm',            // CRM
      'internos',       // Sistemas Internos (con validación de role)
      'marketing',      // Marketing (campañas, formularios, landing)
      'service-desk',   // Service Desk (Soporte)
      'envios',         // Gestión de Envíos
      'bases-y-propiedades', // Bases y Propiedades (Amenidades, Patio, Reservas)
      'access-control', // Nuvex Access — Control de acceso, seguridad y dispositivos
      'comunity',       // Nuvex Access — Comunidad, comunicación y pagos
      'cargo-market',   // Cargo Market — marketplace y cotizador de carga
      'freight-forwarding', // Freight forwarding — Documentación (expediente documental, CC-CONSOLE #1632)
      'gestion-staff',  // Gestión de Staff — Actividades con evidencia
      'finanzas',       // Finanzas — gastos atribuidos por Coding Cloud (CC-CONSOLE #981)
      'b2c',            // B2C — panel interno de Movik (sólo administradores de plataforma)
    ],
  },
};

/**
 * Gets the current environment from Vite's import.meta.env
 */
export const getCurrentEnvironment = (): Environment => {
  const mode = import.meta.env.MODE;
  return mode === 'production' ? 'production' : 'development';
};

/**
 * Gets the visibility configuration for the current environment
 */
export const getVisibilityConfig = (): EnvironmentVisibilityConfig => {
  const env = getCurrentEnvironment();
  return VISIBILITY_CONFIG[env];
};

/**
 * Checks if a specific category should be visible in the current environment
 */
export const isCategoryVisible = (categoryId: string): boolean => {
  const config = getVisibilityConfig();
  return config.visibleCategories.includes(categoryId);
};

/**
 * Checks if a specific section should be visible in the current environment
 */
export const isSectionVisible = (section: keyof SectionVisibility): boolean => {
  const config = getVisibilityConfig();
  return config.sections[section];
};

/**
 * Filters categories based on environment visibility
 * Role-based filtering should be applied separately after this
 */
export const filterCategoriesByEnvironment = <T extends { id: string }>(
  categories: T[]
): T[] => {
  const config = getVisibilityConfig();
  return categories.filter(category => 
    config.visibleCategories.includes(category.id)
  );
};
